import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { User } from 'shared';

import { FunnelService } from '../../../services/funnel.service';
import { PLATINUM_PLAN_SLUG, currentReferral, rememberReferral, validSponsorId } from '../../../services/funnel-intent';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/** Mirrors the backend's MIN_PASSWORD_LENGTH. */
const MIN_PASSWORD_LENGTH = 8;

type Phase = 'opening' | 'password' | 'signing_in' | 'error';

/**
 * Landing of the account / sign-in mail of the Platinum direct checkout
 * (/:lang/join/start?token=…[&from=checkout][&ref=…]). Ported from Global Health.
 *
 * Consuming the token (POST /funnel/membership/start) confirms the address and
 * sets the soft-login cookie. A member without a password sets one here and gets
 * a real session; one who already has a password is signed in from the cookie.
 *
 * Mail links (backend contract): account mail `?token&from=checkout&plan=platinum`,
 * sign-in mail for an existing account `?token&plan=platinum&ref=<sponsor>`.
 * `ref` is stored as the newest referral; see #continue for where the member goes.
 */
@Component({
  selector: 'app-funnel-join-start-page',
  standalone: true,
  imports: [FormsModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-join-start-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelJoinStartPageComponent {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private http       = inject(HttpClient);
  private funnel     = inject(FunnelService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  readonly minLength = MIN_PASSWORD_LENGTH;

  phase = signal<Phase>('opening');
  error = signal<string | null>(null);
  busy  = signal(false);

  firstName = signal<string | null>(null);

  password = '';
  passwordConfirmation = '';

  /** Kept as plain fields + an explicit signal so the template can react to typing. */
  private touched = signal(0);
  passwordValid = computed(() => {
    this.touched();
    return this.password.length >= MIN_PASSWORD_LENGTH &&
           this.password === this.passwordConfirmation;
  });

  onPasswordInput(): void { this.touched.update(n => n + 1); }

  constructor() {
    const token = this.route.snapshot.queryParamMap.get('token')
               ?? this.route.snapshot.params['token']
               ?? '';

    if (!token) {
      this.phase.set('error');
      this.error.set(this.translate.translate('funnel.join_start.missing_token'));
      return;
    }

    const ref = validSponsorId(this.route.snapshot.queryParamMap.get('ref'));
    if (ref) rememberReferral(ref);

    this.funnel.startMembershipFunnel(token, currentReferral(ref))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.firstName.set(res.first_name ?? null);
          // Existing password: sign in from the cookie (the client session still has
          // to be built before a protected route lets them through — adoptSession).
          if (res.account_exists) {
            this.phase.set('signing_in');
            this.adoptSession().subscribe({
              next:  () => this.continue(),
              error: () => this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true })
            });
            return;
          }
          this.phase.set('password');
        },
        error: err => {
          this.phase.set('error');
          this.error.set(err?.error?.error || this.translate.translate('funnel.join_start.link_invalid'));
        }
      });
  }

  submitPassword(): void {
    if (!this.passwordValid() || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);
    const password = this.password;

    this.funnel.setMembershipPassword(password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.phase.set('signing_in');
          // Log in properly rather than riding the funnel cookie.
          this.auth.login({ username: res.email, password })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => { this.busy.set(false); this.continue(); },
              error: () => {
                // The password is set — only the automatic sign-in failed.
                this.busy.set(false);
                this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true });
              }
            });
        },
        error: err => {
          this.busy.set(false);
          this.phase.set('password');
          this.error.set(err?.error?.error || this.translate.translate('funnel.join_start.password_error'));
        }
      });
  }

  /** Turns the soft-login cookie into a client-side session (GET /user + setAuthState). */
  private adoptSession(): Observable<unknown> {
    return this.http.get<{ user: User }>('/user').pipe(
      switchMap(res => from(this.auth.setAuthState(res.user, null)))
    );
  }

  private lang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  /**
   * With `plan=platinum` (account mail and sign-in mail both carry it) the member
   * continues to the purchase: /r, which forwards a signed-in member to the order
   * wizard with the Platinum Card in the cart (product id looked up via GET
   * /products). Without a plan: the dashboard.
   */
  private continue(): void {
    if (this.route.snapshot.queryParamMap.get('plan') === PLATINUM_PLAN_SLUG) {
      this.router.navigate(['/', this.lang(), 'r'], { queryParams: { plan: PLATINUM_PLAN_SLUG }, replaceUrl: true });
      return;
    }
    this.router.navigate(['/', this.lang(), 'dashboard'], { replaceUrl: true });
  }
}
